import {
  KeyboardEvent,
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';

const EMAIL_REGEX = /^.+@.+\..+$/;
const SPLIT_REGEX = /[\s,;]+/;

export interface EmailChipsInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  maxAddresses?: number;
  maxLength?: number;
  disabled?: boolean;
  ariaLabel?: string;
}

interface Chip {
  id: string;
  email: string;
  valid: boolean;
}

let chipKeyCounter = 0;
const nextChipKey = () => `chip-${++chipKeyCounter}`;

const isValidEmail = (s: string) =>
  EMAIL_REGEX.test(s) && s.indexOf('\r') < 0 && s.indexOf('\n') < 0;

const parseValueToChips = (value: string): Chip[] => {
  if (!value) return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .map(email => ({ id: nextChipKey(), email, valid: isValidEmail(email) }));
};

const chipsToValue = (chips: Chip[]) => chips.map(c => c.email).join(', ');

export function EmailChipsInput({
  value,
  onChange,
  id,
  placeholder = 'Type an email and press Enter',
  maxAddresses = 10,
  maxLength = 1000,
  disabled = false,
  ariaLabel,
}: EmailChipsInputProps) {
  const reactId = useId();
  const inputId = id ?? `email-chips-${reactId}`;
  const inputRef = useRef<HTMLInputElement>(null);

  const [chips, setChips] = useState<Chip[]>(() => parseValueToChips(value));
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  // React's recommended "derive state from prop" pattern: sync during render when
  // the parent's value diverges from what we currently render. Comparing against
  // chipsToValue(chips) — not the previous prop — avoids overwriting our own emits.
  const [syncedFrom, setSyncedFrom] = useState(value);
  if (value !== syncedFrom && value !== chipsToValue(chips)) {
    setSyncedFrom(value);
    setChips(parseValueToChips(value));
  }

  const addEmails = useCallback((raw: string) => {
    const parts = raw.split(SPLIT_REGEX).map(s => s.trim()).filter(s => s.length > 0);
    if (parts.length === 0) return;

    const seen = new Set(chips.map(c => c.email.toLowerCase()));
    const next = [...chips];
    for (const p of parts) {
      if (next.length >= maxAddresses) break;
      const key = p.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      next.push({ id: nextChipKey(), email: p, valid: isValidEmail(p) });
    }
    while (next.length > 0 && chipsToValue(next).length > maxLength) {
      next.pop();
    }
    if (next.length === chips.length) return;
    setChips(next);
    onChange(chipsToValue(next));
  }, [chips, onChange, maxAddresses, maxLength]);

  const removeChip = useCallback((chipId: string) => {
    const next = chips.filter(c => c.id !== chipId);
    if (next.length === chips.length) return;
    setChips(next);
    onChange(chipsToValue(next));
  }, [chips, onChange]);

  const commitDraft = useCallback(() => {
    if (!draft.trim()) {
      setDraft('');
      return;
    }
    addEmails(draft);
    setDraft('');
  }, [draft, addEmails]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      // Tab should still move focus when input is empty (don't trap users).
      if (e.key === 'Tab' && !draft.trim()) return;
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft.length === 0 && chips.length > 0) {
      e.preventDefault();
      removeChip(chips[chips.length - 1].id);
    }
  }, [draft, chips, commitDraft, removeChip]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  }, []);

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text || !SPLIT_REGEX.test(text)) return; // single token: let the browser handle it
    e.preventDefault();
    addEmails((draft + ' ' + text));
    setDraft('');
  }, [addEmails, draft]);

  const handleBlur = useCallback((_e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (draft.trim()) commitDraft();
  }, [draft, commitDraft]);

  const handleContainerClick = useCallback(() => {
    if (!disabled) inputRef.current?.focus();
  }, [disabled]);

  const atLimit = chips.length >= maxAddresses;

  return (
    <div
      className={[
        'kst-chips-input',
        focused ? 'kst-chips-input--focused' : '',
        disabled ? 'kst-chips-input--disabled' : '',
      ].filter(Boolean).join(' ')}
      onClick={handleContainerClick}
      role="group"
      aria-label={ariaLabel ?? 'Email recipients'}
    >
      {chips.map(chip => (
        <span
          key={chip.id}
          className={`kst-chips-input__chip${chip.valid ? '' : ' kst-chips-input__chip--invalid'}`}
          title={chip.valid ? chip.email : `${chip.email} — not a valid email address`}
        >
          <span className="kst-chips-input__chip-text">{chip.email}</span>
          <button
            type="button"
            className="kst-chips-input__chip-remove"
            aria-label={`Remove ${chip.email}`}
            onClick={(e) => { e.stopPropagation(); removeChip(chip.id); }}
            disabled={disabled}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        className="kst-chips-input__entry"
        value={draft}
        placeholder={atLimit ? '' : (chips.length === 0 ? placeholder : '')}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        disabled={disabled || atLimit}
        autoComplete="off"
        spellCheck={false}
        aria-describedby={atLimit ? `${inputId}-limit` : undefined}
      />
      {atLimit && (
        <span id={`${inputId}-limit`} className="kst-chips-input__limit">
          {maxAddresses} maximum
        </span>
      )}
    </div>
  );
}
